import React, {
  useState,
  useEffect,
  Fragment,
  useRef,
  useCallback,
} from "react";
import shared from "./Shared";
import ApiClient from "../../methods/api/apiClient";
import methodModel from "../../methods/methods";
import Layout from "../../components/global/layout";
import SelectDropdown from "../../components/common/SelectDropdown";
import loader from "../../methods/loader";
import swal from "sweetalert";
import environment from "../../environment";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  companyName: "",
  mobileNo: "",
  address: "",
  image: "",
  state: "",
  country: "",
  pinCode: "",
};

// ─── FormInput ────────────────────────────────────────────────────
const FormInput = ({
  label,
  name,
  type = "text",
  required,
  textarea,
  value,
  onChange,
  error,
  placeholder,
}) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
        placeholder={placeholder}
        className={`w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all resize-none ${error ? "border-red-400" : "border-gray-200"}`}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${error ? "border-red-400" : "border-gray-200"}`}
      />
    )}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

// ─── PhoneField ───────────────────────────────────────────────────
const PhoneField = ({ label, required, value, onChange, error }) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <PhoneInput
      required={required}
      enableSearch={true}
      country="us"
      autoFormat={false}
      placeholder="Enter phone number"
      value={value}
      countryCodeEditable={true}
      onChange={onChange}
      buttonClass="!border !border-gray-200 !rounded-l-xl !bg-transparent"
      inputClass={`!h-10 !w-full !border !rounded-xl !text-sm !text-gray-800 !pl-12 focus:!ring-2 focus:!ring-[#115B9D]/30 focus:!border-[#115B9D] !transition-all ${error ? "!border-red-400" : "!border-gray-200"
        }`}
    />
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

// ─── Avatar / Image Upload ────────────────────────────────────────
const AvatarUpload = ({ imagePreview, onChange }) => (
  <label className="cursor-pointer">
    <div
      className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${imagePreview ? "border-[#115B9D] bg-[#115B9D17]" : "border-gray-200 hover:border-[#115B9D] hover:bg-[#115B9D17]"}`}
    >
      {imagePreview ? (
        <div className="flex items-center gap-4">
          <img
            src={imagePreview}
            alt="Preview"
            className="size-16 rounded-xl object-cover border border-gray-200"
          />
          <div className="text-left">
            <p className="text-sm text-gray-700 font-medium">Image selected</p>
            <p className="text-xs text-gray-600 mt-0.5">Click to change</p>
            <p className="text-xs text-gray-600">Max 500KB · JPG, PNG, WEBP</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="size-10 rounded-full bg-[#115b9d29] flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#115B9D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" x2="12" y1="3" y2="15" />
            </svg>
          </div>
          <p className="text-sm text-[#115B9D]">Click to upload avatar</p>
          <p className="text-xs text-gray-600">
            Max 500KB · JPG, JPEG, PNG, WEBP
          </p>
        </div>
      )}
      <input
        type="file"
        className="hidden"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={onChange}
      />
    </div>
  </label>
);

// ─── User Avatar Cell ─────────────────────────────────────────────
const UserAvatar = ({ user }) => {
  const initials =
    `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
  return (
    <div className="flex items-center gap-3">
      <div className="size-10 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center overflow-hidden shrink-0">
        {user.image ? (
          <img
            src={methodModel.userImg(user.image)}
            alt={`${user.firstName} ${user.lastName}`}
            className="size-full object-cover rounded-xl"
          />
        ) : (
          <span className="text-sm capitalize font-semibold text-[#115B9D]">
            {initials || "U"}
          </span>
        )}
      </div>
      <div>
        <p className="text-sm capitalize font-medium text-gray-900 whitespace-nowrap">
          {user.firstName} {user.lastName}
        </p>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const UsersListing = () => {
  const [formPage, setFormPage] = useState(false);
  const [users, setUsers] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [filters, setFilter] = useState({
    page: 1,
    count: 10,
    search: "",
    status: "",
  });

  const fetchUsers = async (p = {}) => {
    loader(true);
    const params = {
      page: p.page ?? filters.page,
      count: p.count ?? filters.count,
      search: p.search ?? searchQuery,
      status: p.status ?? filters.status,
      role: environment.user_role_id,
    };
    const query = new URLSearchParams(params).toString();
    await ApiClient.get(`user/listing?${query}`)
      .then((res) => {
        if (res?.success) {
          setUsers(res?.data?.data || []);
          setTotal(res.data.total || 0);
        }
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => fetchUsers({ search: searchQuery }), 500);
    return () => clearTimeout(delay);
  }, [searchQuery]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      swal({
        text: "Image size must be less than 500KB",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      swal({
        text: "Please upload a valid image (JPG, JPEG, PNG, WEBP)",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    setImagePreview(URL.createObjectURL(file));
    loader(true);
    ApiClient.postFormData("upload/image", { file }).then((res) => {
      if (res.fileName) setForm((prev) => ({ ...prev, image: res.fileName }));
      loader(false);
    });
  };

  const validate = () => {
    const errors = {};
    if (!form.firstName?.trim()) errors.firstName = "First name is required";
    if (!form.lastName?.trim()) errors.lastName = "Last name is required";

    // Email validation
    if (!form.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = "Please enter a valid email address";
    }

    // Mobile validation
    if (form.mobileNo && form.mobileNo.replace(/\D/g, "").length < 7) {
      errors.mobileNo = "Please enter a valid mobile number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setImagePreview(null);
    setFormErrors({});
    setIsEditMode(false);
    setEditingId(null);
    setFormPage(true);
  };

  const openEditModal = async (id) => {
    loader(true);
    await ApiClient.get(`user/profile?id=${id}`)
      .then((res) => {
        if (res?.success) {
          const d = res.user_data;
          setForm({
            firstName: d.firstName || "",
            lastName: d.lastName || "",
            email: d.email || "",
            companyName: d.companyName || "",
            mobileNo: d.mobileNo || "",
            address: d.address || "",
            image: d.image || "",
            state: d.state || "",
            country: d.country || "",
            pinCode: d.pinCode || "",
          });
          setImagePreview(d.image ? methodModel.userImg(d.image) : null);
          setFormErrors({});
          setIsEditMode(true);
          setEditingId(id);
          setFormPage(true);
        }
      })
      .catch(console.error);
    loader(false);
  };

  const closeModal = () => {
    setFormPage(false);
    setForm(emptyForm);
    setImagePreview(null);
    setFormErrors({});
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    loader(true);
    const payload = { ...form, role: environment.user_role_id };
    if (isEditMode) {
      delete payload.role;
      delete payload.email;
    }
    const endpoint = isEditMode ? "user/profile" : "user/add-user";
    await ApiClient[isEditMode ? "put" : "post"](
      endpoint,
      isEditMode ? { ...payload, id: editingId } : payload,
    )
      .then((res) => {
        if (res?.success) {
          swal({
            text: `User ${isEditMode ? "updated" : "added"} successfully.`,
            icon: "success",
            className: "smallModal",
          });
          closeModal();
          fetchUsers();
        }
      })
      .catch(console.error);
    loader(false);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This user will be permanently deleted.",
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      await ApiClient.delete(`user/delete?id=${id}`)
        .then(async (res) => {
          if (res?.success) {
            await fetchUsers();
            swal({
              text: "User deleted successfully.",
              icon: "success",
              className: "smallModal",
            });
          }
        })
        .catch(console.error);
      loader(false);
    });
  };

  const statusChange = (item, status) => {
    swal({
      title: "Are you sure?",
      text: `Do you want to ${status === "active" ? "activate" : "deactivate"} this User?`,
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Yes"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.put(`user/profile`, {
          id: item._id || item.id,
          status,
        });
        if (res?.success) {
          await fetchUsers();
          swal({
            text: `User ${status === "active" ? "activated" : "deactivated"} successfully.`,
            icon: "success",
            className: "smallModal",
          });
        }
      } catch (err) {
        console.error(err);
      }
      loader(false);
    });
  };

  const changestatus = (e, key) => {
    setFilter((prev) => ({ ...prev, [key]: e, page: 1 }));
    fetchUsers({ [key]: e, page: 1 });
  };

  const handlePageChange = (newPage) => {
    const maxPage = Math.ceil(total / filters.count);
    if (newPage < 1 || newPage > maxPage) return;
    setFilter((prev) => ({ ...prev, page: newPage }));
    fetchUsers({ page: newPage });
  };

  return (
    <Layout>
      {!formPage ? (
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
          {/* ─── Page Header ─── */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
            <div className="flex flex-col gap-1">
              <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
                Customers
              </h2>
              <p className="text-sm sm:text-md text-gray-600">
                {total} customer{total !== 1 ? "s" : ""} total
              </p>
            </div>
            <button
              onClick={openAddModal}
              className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add Customer
            </button>
          </div>

          <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
            {/* ─── Filters ─── */}
            <div className="flex justify-between flex-wrap gap-3 mb-4">
              <div className="relative w-full sm:w-[350px]">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <SelectDropdown
                  id="statusDropdown"
                  theme="search"
                  displayValue="name"
                  placeholder="All status"
                  intialValue={filters?.status}
                  result={(e) => changestatus(e.value, "status")}
                  isClearable={false}
                  crossIcon={filters?.status ? true : false}
                  options={shared.filterStatus}
                />
              </div>
            </div>

            {/* ─── Table ─── */}
            {/* ─── Table ─── */}

            {/* Desktop Table */}
            <div className="hidden md:block overflow-auto scrollbar border border-gray-200 rounded-xl">
              <table className="w-full min-w-[700px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {[
                      "Actions",
                      "Full Name",
                      "Email",
                      "Location",
                      "Status",
                    ].map((h) => (
                      <th key={h} className="text-left py-3 px-5">
                        <span className="text-sm text-gray-700 font-[400] whitespace-nowrap">
                          {h}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-16">
                        <div className="flex flex-col items-center gap-3">
                          <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-gray-600"
                            >
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-500">
                            No customers found
                          </p>
                          <button
                            onClick={openAddModal}
                            className="text-[#115B9D] text-sm hover:underline"
                          >
                            Add your first customer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users?.map((item, index) => (
                      <tr
                        key={item.id || item._id || index}
                        className="hover:bg-gray-50/60 transition-colors"
                      >
                        {/* Actions */}
                        <td className="py-3 px-5">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(item.id || item._id)}
                              title="Edit"
                              className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id || item._id)}
                              title="Delete"
                              className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18" />
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                <line x1="10" x2="10" y1="11" y2="17" />
                                <line x1="14" x2="14" y1="11" y2="17" />
                              </svg>
                            </button>
                          </div>
                        </td>

                        {/* User */}
                        <td className="py-3 px-5">
                          <UserAvatar user={item} />
                        </td>

                        {/* Email */}
                        <td className="py-3 px-5">
                          <p className="text-sm text-gray-800 whitespace-nowrap">
                            {item.email || (
                              <span className="text-gray-600">—</span>
                            )}
                          </p>
                        </td>

                        {/* Location */}
                        <td className="py-3 px-5">
                          <p className="text-sm text-gray-800 whitespace-nowrap">
                            {[item.state, item.country]
                              .filter(Boolean)
                              .join(", ") || (
                                <span className="text-gray-600">—</span>
                              )}
                          </p>
                          {item.pinCode && (
                            <p className="text-xs text-gray-600">
                              {item.pinCode}
                            </p>
                          )}
                        </td>

                        {/* Status */}
                        <td className="py-3 px-5 cursor-pointer">
                          {item.status === "active" ? (
                            <span
                              onClick={() => statusChange(item, "deactive")}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-xs"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <path d="m9 12 2 2 4-4" />
                              </svg>
                              Active
                            </span>
                          ) : (
                            <span
                              onClick={() => statusChange(item, "active")}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-xs"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                              </svg>
                              Deactive
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden">
              {users?.length === 0 ? (
                <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                  <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-600"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">No customers found</p>
                  <button
                    onClick={openAddModal}
                    className="text-[#115B9D] text-sm hover:underline"
                  >
                    Add your first customer
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {users?.map((item, index) => (
                    <div
                      key={item.id || item._id || index}
                      className="border border-gray-200 rounded-xl p-4 bg-white"
                    >
                      {/* Top row: User + Actions */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <UserAvatar user={item} />
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => openEditModal(item.id || item._id)}
                            title="Edit"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id || item._id)}
                            title="Delete"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              <line x1="10" x2="10" y1="11" y2="17" />
                              <line x1="14" x2="14" y1="11" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Email</span>
                        <p className="text-sm text-gray-800 text-right break-all">
                          {item.email || (
                            <span className="text-gray-600">—</span>
                          )}
                        </p>
                      </div>

                      {/* Location */}
                      <div className="flex justify-between items-start py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Location</span>
                        <div className="text-right">
                          <p className="text-sm text-gray-800">
                            {[item.state, item.country]
                              .filter(Boolean)
                              .join(", ") || (
                                <span className="text-gray-600">—</span>
                              )}
                          </p>
                          {item.pinCode && (
                            <p className="text-xs text-gray-600">
                              {item.pinCode}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Status</span>
                        {item.status === "active" ? (
                          <span
                            onClick={() => statusChange(item, "deactive")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-xs cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="m9 12 2 2 4-4" />
                            </svg>
                            Active
                          </span>
                        ) : (
                          <span
                            onClick={() => statusChange(item, "active")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-xs cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            Deactive
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Pagination ─── */}
            {total > filters.count && (
              <div className="flex flex-wrap gap-y-2 gap-x-5 items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {(filters.page - 1) * filters.count + 1} to{" "}
                  {Math.min(filters.page * filters.count, total)} of {total}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handlePageChange(filters.page - 1)}
                    disabled={filters.page === 1}
                    className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                  >
                    Prev
                  </button>
                  {Array.from(
                    { length: Math.ceil(total / filters.count) },
                    (_, i) => i + 1,
                  ).map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${Number(filters.page) === Number(p) ? "bg-[#115B9D] text-white" : "border"}`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(filters.page + 1)}
                    disabled={filters.page === Math.ceil(total / filters.count)}
                    className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="">
          <div className="">
            {/* ─── Page Header ─── */}
            <div className="flex items-center gap-3 mb-8">
              <div className="size-10 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#115B9D"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-[600] text-gray-800 tracking-tight">
                  {isEditMode ? "Edit Customer" : "Add New Customer"}
                </h1>
                <p className="text-sm text-gray-600 mt-0.5">
                  {isEditMode
                    ? "Update the customer details below"
                    : "Fill in the details to add a new customer"}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* ── Avatar Upload ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-4">
                  Profile Photo
                </p>
                <AvatarUpload
                  imagePreview={imagePreview}
                  onChange={handleImageChange}
                />
              </div>

              {/* ── Personal Info ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-5">
                  Personal Info
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid grid-cols-2 gap-4 sm:contents">
                  <FormInput
                    label="First Name"
                    name="firstName"
                    required
                    placeholder="John"
                    value={form.firstName}
                    onChange={handleChange}
                    error={formErrors.firstName}
                  />
                  <FormInput
                    label="Last Name"
                    name="lastName"
                    required
                    placeholder="Doe"
                    value={form.lastName}
                    onChange={handleChange}
                    error={formErrors.lastName}
                  />
                  </div>
                  <FormInput
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    error={formErrors.email}
                    disabled={isEditMode}
                  />
                  <PhoneField
                    label="Mobile Number"
                    // required
                    value={form.mobileNo}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, mobileNo: e }));
                      if (formErrors.mobileNo)
                        setFormErrors((prev) => ({ ...prev, mobileNo: "" }));
                    }}
                    error={formErrors.mobileNo}
                  />
                </div>
              </div>

              {/* ── Address ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-5">
                  Address
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Street Address */}
                  <div className="sm:col-span-2">
                    <FormInput
                      label="Street Address"
                      name="address"
                      textarea
                      placeholder="123 Main St, Apt 4B"
                      value={form.address}
                      onChange={handleChange}
                      error={formErrors.address}
                    />
                  </div>

                  {/* State */}
                  <FormInput
                    label="State / Province"
                    name="state"
                    placeholder="California"
                    value={form.state}
                    onChange={handleChange}
                    error={formErrors.state}
                  />

                  {/* Country + Pin */}
                  <div className="grid grid-cols-2 gap-4 sm:contents">
                    <FormInput
                      label="Country"
                      name="country"
                      placeholder="United States"
                      value={form.country}
                      onChange={handleChange}
                      error={formErrors.country}
                    />

                    <FormInput
                      label="Pin / ZIP Code"
                      name="pinCode"
                      placeholder="90210"
                      value={form.pinCode}
                      onChange={handleChange}
                      error={formErrors.pinCode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Action Buttons ── */}
            <div className="flex gap-2 flex-wrap items-center justify-end mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {isEditMode ? (
                    <>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </>
                  ) : (
                    <path d="M12 5v14M5 12h14" />
                  )}
                </svg>
                {isEditMode ? "Update Customer" : "Add Customer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UsersListing;
